import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { Container } from 'react-bootstrap';

import { useToast } from '../../hooks/toast';
import { usePerfil } from '../../hooks/perfil';
import { useMedico } from '../../hooks/medico';
import { useClinica } from '../../hooks/clinica';
import { useAuth } from '../../hooks/auth';

import SelectSync, { IOption } from '../../components/SelectSync';

import Modal from '../../components/Modal';
import Button from '../../components/Button';

import getValidationErrors from '../../utils/getValidationErrors';
import ListaAgendas, { IDadosLista, RefObject } from './ListaAgendas';
import AgendasCadastroPagina, { IConfiguracaoAgenda } from './AgendasCadastro';

import { TIPO_USUARIO } from '../../utils/funcoes';

export interface IFormBusca {
  medico?: string;
  perfil?: string;
  clinica?: string;
  situacao?: string;
  online?: string;
  atende?: string;
}

const AgendaCadastro: React.FC = () => {
  const listaRef = useRef<RefObject>(null);
  const history = useHistory();
  const { user } = useAuth();
  const { addToast } = useToast();
  const { optionsPerfil, buscaPerfil } = usePerfil();
  const { optionsMedico, buscaOptionsMedico } = useMedico();
  const { optionsClinica } = useClinica();
  const [dadosForm, setDadosForm] = useState<IFormBusca | undefined>(undefined);

  const formRefBusca = useRef<FormHandles>(null);

  const [showModalCadastro, setShowModalCadastro] = useState(false);

  const [optionPerfil, setOptionsPerfil] = useState<IOption[]>([]);

  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.SUPERVISOR) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  const initialData = {
    situacao: 'true',
    clinica: user.UsuClinica.toString(),
  };

  useEffect(() => {
    async function loadPerfis(): Promise<void> {
      await buscaPerfil();
    }
    if (!optionsPerfil || Object.keys(optionsPerfil).length === 0) loadPerfis();
  }, [buscaPerfil, optionsPerfil]);

  useEffect(() => {
    async function loadMedicos(): Promise<void> {
      await buscaOptionsMedico();
    }
    if (!optionsMedico || Object.keys(optionsMedico).length === 0)
      loadMedicos();
  }, [buscaOptionsMedico, optionsMedico]);

  useEffect(() => {
    try {
      setOptionsPerfil(optionsPerfil.optionPerfis);
    } catch (error) {
      addToast({
        title: 'Não foi possível buscar perfis',
        type: 'error',
      });
    }
  }, [addToast, optionsPerfil]);

  const handleSubmitBusca = useCallback(async (dados: IFormBusca) => {
    try {
      formRefBusca.current?.setErrors({});
      const schema = Yup.object().shape({
        clinica: Yup.string(),
        perfil: Yup.string(),
        medico: Yup.string(),
        situacao: Yup.string().nullable(true),
        online: Yup.string().nullable(true),
        atende: Yup.string().nullable(true),
      });

      await schema.validate(dados, {
        abortEarly: false,
      });

      setDadosForm(dados);
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error);
        formRefBusca.current?.setErrors(errors);
      }
    }
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);

  const handleAdd = useCallback(
    async (
      dadosConfiguracaoAgenda: IConfiguracaoAgenda,
      dadosLista: IDadosLista,
      conCodigo: number | undefined,
    ) => {
      if (listaRef.current) {
        await listaRef.current.addAgenda(
          dadosConfiguracaoAgenda,
          dadosLista,
          conCodigo,
        );
        setShowModalCadastro(false);
      }
    },
    [],
  );

  return (
    <Container fluid className="mt-2">
      {showModalCadastro && (
        <Modal
          show={showModalCadastro}
          titulo="Cadastro de Agendas"
          handleFecharModal={() => setShowModalCadastro(false)}
          large
        >
          <AgendasCadastroPagina addAgenda={handleAdd} />
        </Modal>
      )}
      <div className="bg-white border p-2 mb-2">
        <div className="d-flex justify-content-between mb-2">
          <h2 id="topoBusca">Agendas</h2>
          <div>
            <Button
              type="button"
              variant="success"
              onClick={() => {
                setShowModalCadastro(true);
              }}
            >
              Nova Agenda
            </Button>
          </div>
        </div>

        <Form
          ref={formRefBusca}
          initialData={initialData}
          onSubmit={handleSubmitBusca}
        >
          <div className="row no-gutters">
            <div className="col-md-2 mr-1">
              <SelectSync
                name="perfil"
                label="Perfil"
                placeholder="Selecione"
                isClearable
                handleSelectChange={handleOnChangeVazio}
                options={optionPerfil}
              />
            </div>
            <div className="col-md-2 mr-1">
              <SelectSync
                name="medico"
                label="Médico"
                placeholder="Selecione"
                isClearable
                handleSelectChange={handleOnChangeVazio}
                options={optionsMedico}
              />
            </div>
            <div className="col-md-2 mr-1">
              <SelectSync
                name="atende"
                label="Atende Consultório"
                placeholder="Selecione"
                isClearable
                handleSelectChange={handleOnChangeVazio}
                options={[
                  { label: 'Todos', value: '' },
                  { label: 'Sim', value: 'true' },
                  { label: 'Não', value: 'false' },
                ]}
              />
            </div>
            <div className="col-md-2 mr-1">
              <SelectSync
                name="clinica"
                label="Clínica"
                placeholder="Selecione"
                isClearable
                handleSelectChange={handleOnChangeVazio}
                options={optionsClinica}
                disabled={user.UsuTipo !== TIPO_USUARIO.ADMINISTRADOR}
              />
            </div>
            <div className="col-md-1 mr-1">
              <SelectSync
                name="situacao"
                label="Situação"
                placeholder="Selecione"
                isClearable
                handleSelectChange={handleOnChangeVazio}
                options={[
                  { label: 'Todos', value: '' },
                  { label: 'Ativo', value: 'true' },
                  { label: 'Desativado', value: 'false' },
                ]}
              />
            </div>
            <div className="col-md-1 mr-1">
              <SelectSync
                name="online"
                label="On-line"
                placeholder="Selecione"
                isClearable
                handleSelectChange={handleOnChangeVazio}
                options={[
                  { label: 'Todos', value: '' },
                  { label: 'Sim', value: 'true' },
                  { label: 'Não', value: 'false' },
                ]}
              />
            </div>
            <div className="col-md-1">
              <div className="input-group-append mt-3">
                <Button type="submit" variant="secondary-outline">
                  BUSCAR
                </Button>
              </div>
            </div>
          </div>
        </Form>
      </div>

      <div className="bg-white border p-2">
        <ListaAgendas ref={listaRef} dados={dadosForm} />
      </div>
    </Container>
  );
};

export default AgendaCadastro;
